import Header from "@/components/header";
import { PlaceAnAppointmentPayload } from "@/services/appointment/type";
import {
  Alert,
  Button,
  Center,
  CloseButton,
  Group,
  InputBase,
  Loader,
  Stack,
  Text,
} from "@mantine/core";

import CustomDrawer from "@/components/drawer";
import CustomStepper from "@/components/stepper";
import CustomerItem from "@/modules/customer/list/components/customerItem";
import SearchUsers from "@/modules/messages/selectUsers/components/searchUsers";
import AddCustomer from "@/modules/schedule/addCustomer";
import { CustomerInfo } from "@/services/customer/type";
import PersianUtil from "@/services/persianUtiles/numberPersianUtiles";
import { useForm, yupResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronLeft, IconSearch } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import useGetCustomerByPhoneNumber from "../../customer/hooks/useGetCustomerByPhoneNumber";
import { stepperAppointment } from "../type";
import { notifications } from "@mantine/notifications";
import useScheduleStore from "@/store/schedule";

const schema = Yup.object().shape({
  title: Yup.string().required("لطفا نام سرویس را وارد نمایید"),
});

function SelectCustomer() {
  const [filter, setFilter] = useState("");
  const [opened, { open, close }] = useDisclosure(false);
  const [openedUser, { open: openUser, close: closeUser }] =
    useDisclosure(false);
  const { setCustomer } = useScheduleStore();
  const { state } = useLocation();

  const [selectedItems, setSelectedItems] = useState<CustomerInfo[]>([]);
  const [phoneNumber, setPhoneNumber] = useState("");
  const navigate = useNavigate();

  const {
    data,
    isLoading: isLoadingSearch,
    isSuccess,
    refetch,
  } = useGetCustomerByPhoneNumber({
    phoneNumber: filter,
  });

  useEffect(() => {
    setFilter(phoneNumber || "");
    setSelectedItems([]);
  }, [phoneNumber]);

  useEffect(() => {
    if (isSuccess && !data) {
      open();
    }
  }, [isSuccess]);

  const form = useForm<PlaceAnAppointmentPayload>({
    initialValues: {},
    validate: yupResolver(schema),
  });

  const submit = () => {
    if (!data) {
      notifications.show({
        message: "لطفا مشتری را انتخاب نمایید",
        color: "yellow",
      });
      return;
    }
    setCustomer({
      selectCustomer: data,
    });
    navigate("/dashboard/schedule/selectService", {
      state,
    });
  };

  useEffect(() => {
    if (selectedItems.length > 0)
      setPhoneNumber(selectedItems[0]?.phoneNumber || "");
  }, [selectedItems]);

  return (
    <>
      <Stack h="100svh">
        <Header showBack showHome title="افزودن نوبت" />
        <CustomStepper steppers={stepperAppointment} active={0} />
        <Stack
          styles={{
            root: {
              flexGrow: 1,
              overflow: "hidden",
              overflowY: "auto",
            },
          }}
          px="lg"
        >
          <form
            onSubmit={form.onSubmit(submit)}
            name="add-customer"
            className="w-100"
            id="add-customer"
          >
            <Stack>
              <Alert>
                <Text size="xs" ta="center">
                  شماره همراه کاربر را وارد کنید. جستجو به صورت خودکار بر روی
                  شماره تلفن های ثبت شده در سامانه انجام میشود.
                </Text>
              </Alert>
              <InputBase
                size="md"
                type="tel"
                dir="ltr"
                styles={{
                  input: {
                    textAlign: "left",
                    paddingLeft: "16px",
                  },
                }}
                value={phoneNumber || ""}
                maxLength={11}
                label="شماره تلفن مشتری را وارد نمایید"
                leftSection={
                  isLoadingSearch ? (
                    <Loader size="xs" type="dots" />
                  ) : phoneNumber ? (
                    <CloseButton onClick={() => setPhoneNumber("")} />
                  ) : (
                    ""
                  )
                }
                withAsterisk
                onChange={(e) => {
                  setPhoneNumber(
                    PersianUtil.currentNumberToEnglish(e.target.value)
                  );
                }}
              />
              {isSuccess && data ? (
                <>
                  <CustomerItem customer={data || {}} showDetails={false} />
                </>
              ) : (
                isSuccess &&
                !opened &&
                phoneNumber && (
                  <Alert>
                    <Stack>
                      <Center>
                        <Text size="xs" ta="center">
                          کاربری با این شماره در سامانه شما ثبت نشده است ، لطفا
                          ابتدا کاربر را اضافه کنید
                        </Text>
                      </Center>
                      <Center>
                        <Button
                          w="100px"
                          size="compact-xs"
                          variant="light"
                          onClick={open}
                        >
                          افزودن کاربر +
                        </Button>
                      </Center>
                    </Stack>
                  </Alert>
                )
              )}
            </Stack>
            {!data && (
              <Stack mt="md">
                <Center>
                  <Button variant="light" onClick={openUser}>
                    <Group gap="xs">
                      <Text size="xs">انتخاب از مشتریان</Text>
                      <IconSearch size={15} />
                    </Group>
                  </Button>
                </Center>
              </Stack>
            )}
          </form>
        </Stack>
        <Stack px="lg">
          <Button
            type="submit"
            size="md"
            my="md"
            fullWidth
            form="add-customer"
            onClick={submit}
            loading={isLoadingSearch}
          >
            <Group>
              <Text>مرحله بعد</Text>
              <IconChevronLeft size={20} />
            </Group>
          </Button>
        </Stack>
      </Stack>
      <CustomDrawer
        opened={opened}
        onClose={close}
        close={close}
        title="ابتدا کاربر را ثبت نمایید"
        radius="md"
        position="bottom"
        custom="edit-user"
      >
        <AddCustomer
          close={close}
          phoneNumber={phoneNumber}
          refetch={refetch}
        />
      </CustomDrawer>
      <CustomDrawer
        opened={openedUser}
        onClose={closeUser}
        close={closeUser}
        title="افزودن اعضا"
        radius="md"
        position="bottom"
        size="lg"
        custom="select-customer"
      >
        <SearchUsers
          selectedUsers={selectedItems}
          close={closeUser}
          setSelectedUsers={setSelectedItems}
        />
      </CustomDrawer>
    </>
  );
}

export default SelectCustomer;
